import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/shared/composables/use-fetch'
import { addItems, clearItems } from '@/modules/shared/utils/store'
import { EntityMap, InProgress, AllEntity, Entity } from '../types'
import { capitalize } from 'lodash'
import { useFetch as useFetchv4 } from '@/modules/core/composables/useFetch'

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

export const useEntityStore = defineStore('admin/entityStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/admin`)

  const items = ref<EntityMap>(new Map())
  const entities = computed(() => Array.from(items.value.values()))

  const fetchEntities = async () => {
    clearItems(items)

    const { data, error } = await useFetch(`${baseUrl.value}/entities`)
      .get()
      .json<{ data: Entity[]; has_more: boolean }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
    }

    addItems(
      items,
      data.value.data.map((item) => ({ ...item, _key: item.entity_type })),
    )
  }

  // This is to load the spv or fund that has in-progress status
  const inProgressEntities = ref<InProgress[]>([])

  const columnDefinitions = computed(() => {
    const firstRow = inProgressEntities.value[0]
    if (!firstRow) return []

    return Object.entries(firstRow)
      .filter(([key, _]) => key.startsWith('column_'))
      .map(([columnKey, columnValue]) => {
        const index = columnKey.split('_')[1] // Extract "1" from "column_1"
        const completedKey = `completed_${index}`

        return {
          key: completedKey,
          name: capitalize(String(columnValue || '')),
          type: 'string',
          align: 'left',
          is_visible: true,
          fixed: false,
        }
      })
  })

  const allEntities = ref<AllEntity[]>([])

  const fetchAllEntities = async () => {
    allEntities.value = []
    allEntities.value = await useFetchv4(`/v4/${baseUrl.value}/list`).get<AllEntity[]>()
  }

  const fetchInProgress = async (entity_type) => {
    inProgressEntities.value = []
    inProgressEntities.value = await useFetchv4(`/v4/${baseUrl.value}/in_progress_entities/${entity_type}`).get<
      InProgress[]
    >()
  }

  return {
    items,
    entities,
    fetchEntities,
    allEntities,
    inProgressEntities,
    columnDefinitions,
    fetchInProgress,
    fetchAllEntities,
  }
})
